import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { OnboardingTask } from "@forgepoint/shared";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";

interface TaskDescriptionSheetProps {
  task: OnboardingTask | null;
  onClose: () => void;
}

/**
 * Slide-over for editing a task's markdown description from settings. Open
 * while `task` is non-null; saving persists the description (blank clears it)
 * and closes the sheet.
 */
export function TaskDescriptionSheet({
  task,
  onClose,
}: TaskDescriptionSheetProps) {
  const updateTaskFields = useOnboardingStore(
    (state) => state.updateTaskFields,
  );
  const editState = useOnboardingStore((state) =>
    task ? state.taskEditStateById.get(task.id) : undefined,
  );

  const [description, setDescription] = useState("");

  useEffect(() => {
    setDescription(task?.description ?? "");
  }, [task?.id, task?.description]);

  const handleSave = async () => {
    if (!task) {
      return;
    }
    const saved = await updateTaskFields(task.id, {
      description: description.trim() ? description : null,
    });
    if (saved) {
      onClose();
    }
  };

  return (
    <Sheet open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col overflow-hidden sm:w-[640px] sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle>{task?.name}</SheetTitle>
          <SheetDescription>
            Markdown description shown when the task is opened from the
            onboarding page or the inbox.
          </SheetDescription>
        </SheetHeader>
        {task && (
          <>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              placeholder="Add details, links, and steps in markdown..."
              className="min-h-0 flex-1"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              {editState?.error && (
                <span className="text-sm text-destructive">
                  {editState.error}
                </span>
              )}
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={editState?.isSaving}>
                Save
                {editState?.isSaving && (
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                )}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
